import { z } from "zod";
import { channelFeedThreadSchema, } from "./channel-feed.js";
/*
 * Twin shares (docs/specs/twin-shares.md): a person shares an email, a web page, a Twin thing or an excerpt with named
 * colleagues or with one channel, and they discuss it in the share's one thread. A share names its subject the way
 * Desk's deep links do, but a name grants nothing: who may read a share is the share's own check
 * (`packages/twin-access/src/share-access-sql.ts`), and a share never grants access to what it is about.
 */
const id = z.string().trim().min(1).max(200);
export const twinShareSubjectKindSchema = z.enum(["email", "mailbox_message", "web_page", "twin", "excerpt"]);
/** The Twin things a share can be about. The sharer must be able to open the thing when sharing it. */
export const twinShareTwinTypeSchema = z.enum(["record", "session", "entity", "file", "channel"]);
/** A Message-ID as a name: trimmed and without its angle brackets, at most RFC 5322's 998 characters. */
export const TWIN_SHARE_MESSAGE_ID_MAX = 998;
export const TWIN_SHARE_WEB_ADDRESS_MAX = 2_048;
/** People in a share, the sharer included. */
export const TWIN_SHARE_MAX_PEOPLE = 20;
export const TWIN_SHARE_TITLE_MAX = 300;
export const TWIN_SHARE_TEXT_MAX_BYTES = 32 * 1024;
export const TWIN_SHARE_QUOTE_MAX = 4_000;
/** A snapshot's files: the sharer's own uploads for a share (`twin.files.upload` with `purpose: "share"`). */
export const TWIN_SHARE_MAX_FILES = 10;
const messageIdInput = z.string().min(1).max(TWIN_SHARE_MESSAGE_ID_MAX + 2);
/**
 * What a share is about, as the client names it. `excerpt` is something that exists only on the person's Mac (a
 * draft, a work-log thread, an agent session): nothing of it leaves but the snapshot, and every excerpt share is new.
 */
export const twinShareSubjectSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("email"), messageId: messageIdInput }).strict(),
    z.object({ kind: z.literal("mailbox_message"), connectionId: id, messageId: messageIdInput }).strict(),
    z.object({ kind: z.literal("web_page"), url: z.string().min(1).max(8_192) }).strict(),
    z.object({
        kind: z.literal("twin"),
        type: twinShareTwinTypeSchema,
        id,
        /** The record's channel; only a record names one. */
        channelId: id.optional(),
    }).strict().refine((subject) => (subject.type === "record") === (subject.channelId !== undefined), {
        message: "A record names its channel, and nothing else does.",
    }),
    z.object({ kind: z.literal("excerpt") }).strict(),
]);
/** A Message-ID as a share names it, or null when it is not one: no whitespace, brackets or control characters. */
export function normaliseTwinShareMessageId(value) {
    let messageId = value.trim();
    if (messageId.startsWith("<") && messageId.endsWith(">"))
        messageId = messageId.slice(1, -1).trim();
    if (!messageId || messageId.length > TWIN_SHARE_MESSAGE_ID_MAX)
        return null;
    return /[\s<>\p{Cc}]/u.test(messageId) ? null : messageId;
}
const TRACKING_PARAMETERS = new Set(["gclid", "fbclid", "mc_cid", "mc_eid", "msclkid"]);
const SECRET_PARAMETERS = new Set(["token", "access_token", "id_token", "code", "key", "api_key", "sig", "signature",
    "password", "secret", "session"]);
/** A query parameter that looks like a credential: a share refuses its address rather than store it. */
export function twinShareSecretParameter(name) {
    const lower = name.toLowerCase();
    return SECRET_PARAMETERS.has(lower) || lower.startsWith("x-amz-") || lower.startsWith("x-goog-");
}
function trackingParameter(name) {
    const lower = name.toLowerCase();
    return lower.startsWith("utm_") || TRACKING_PARAMETERS.has(lower);
}
/**
 * A web page's address as a share names it: `http` or `https` only, no user name or password, scheme and host
 * lower-cased, the default port and the fragment dropped, known tracking parameters dropped and the rest sorted by name,
 * at most 2,048 characters. An address whose query looks like it carries a secret is refused, so Desk offers to share a
 * quote without it.
 */
export function normaliseTwinShareWebAddress(value) {
    let url;
    try {
        url = new URL(value.trim());
    }
    catch {
        return { ok: false, reason: "address_invalid" };
    }
    if (url.protocol !== "http:" && url.protocol !== "https:")
        return { ok: false, reason: "address_scheme" };
    if (url.username || url.password)
        return { ok: false, reason: "address_credentials" };
    const parameters = [...url.searchParams.entries()];
    if (parameters.some(([name]) => twinShareSecretParameter(name)))
        return { ok: false, reason: "address_secret_parameter" };
    // A stable sort by name: repeated parameters keep their order.
    const kept = parameters.filter(([name]) => !trackingParameter(name))
        .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
    url.hash = "";
    url.search = "";
    for (const [name, parameterValue] of kept)
        url.searchParams.append(name, parameterValue);
    const address = url.toString();
    return address.length > TWIN_SHARE_WEB_ADDRESS_MAX ? { ok: false, reason: "address_too_long" } : { ok: true, address };
}
/**
 * The subject in its normal form and its key, `email:<message-id>`, `mailbox:<connection id>:<message-id>`,
 * `web:<address>` or `twin:<type>:<id>`, which finds the same subject again; an excerpt has none, so it never matches.
 */
export function normaliseTwinShareSubject(subject) {
    switch (subject.kind) {
        case "email":
        case "mailbox_message": {
            const messageId = normaliseTwinShareMessageId(subject.messageId);
            if (!messageId)
                return { ok: false, reason: "message_id_invalid" };
            return subject.kind === "email"
                ? { ok: true, subject: { kind: "email", messageId }, key: `email:${messageId}` }
                : { ok: true, subject: { kind: "mailbox_message", connectionId: subject.connectionId, messageId },
                    key: `mailbox:${subject.connectionId}:${messageId}` };
        }
        case "web_page": {
            const normalised = normaliseTwinShareWebAddress(subject.url);
            if (!normalised.ok)
                return normalised;
            return { ok: true, subject: { kind: "web_page", url: normalised.address }, key: `web:${normalised.address}` };
        }
        case "twin":
            return { ok: true, subject, key: `twin:${subject.type}:${subject.id}` };
        case "excerpt":
            return { ok: true, subject, key: null };
    }
}
/** A mailbox in an email's header lines. Addresses are stored apart, so an erasure request finds every one. */
export const twinShareMailboxSchema = z.object({
    name: z.string().trim().min(1).max(300).optional(),
    address: z.string().trim().min(3).max(320).regex(/^[^\s@<>]+@[^\s@<>]+$/u),
}).strict();
const snapshotTextSchema = z.string().min(1).refine((text) => new TextEncoder().encode(text).byteLength <= TWIN_SHARE_TEXT_MAX_BYTES, { message: `Snapshot text is at most ${TWIN_SHARE_TEXT_MAX_BYTES} bytes; send more as a file.` });
/** What the sharer chose to show. Desk leaves out routing and signing headers; the Twin stores only these. */
export const twinShareSnapshotInputSchema = z.object({
    title: z.string().trim().min(1).max(TWIN_SHARE_TITLE_MAX).optional(),
    from: twinShareMailboxSchema.optional(),
    to: z.array(twinShareMailboxSchema).max(50).optional(),
    cc: z.array(twinShareMailboxSchema).max(50).optional(),
    /** The email's date, in milliseconds since the epoch. */
    sentAt: z.number().int().nonnegative().optional(),
    text: snapshotTextSchema.optional(),
    quote: z.string().trim().min(1).max(TWIN_SHARE_QUOTE_MAX).optional(),
    fileIds: z.array(id).min(1).max(TWIN_SHARE_MAX_FILES).optional(),
}).strict().refine((snapshot) => Object.values(snapshot).some((value) => value !== undefined), {
    message: "A snapshot shows something.",
});
/** One of a snapshot's files, read through the share (`twin.share-files.content`). */
export const twinShareFileSchema = z.object({
    id,
    fileName: z.string(),
    contentType: z.string(),
    byteSize: z.number().int().nonnegative(),
}).strict();
export const twinShareSnapshotSchema = z.object({
    title: z.string().nullable(),
    from: twinShareMailboxSchema.nullable(),
    to: z.array(twinShareMailboxSchema),
    cc: z.array(twinShareMailboxSchema),
    sentAt: z.number().nullable(),
    text: z.string().nullable(),
    quote: z.string().nullable(),
    files: z.array(twinShareFileSchema),
    /** Who supplied the snapshot, and when: the view says so beside it. */
    suppliedBy: id,
    suppliedAt: z.number(),
}).strict();
/** Its people, or one internal channel, never both. */
export const twinShareAudienceInputSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("people"), userIds: z.array(id).min(1).max(TWIN_SHARE_MAX_PEOPLE) }).strict(),
    z.object({ kind: z.literal("channel"), channelId: id }).strict(),
]);
/** A subject that exists outside the Twin needs a snapshot: the share is how people without it see it. */
export function twinShareNeedsSnapshot(kind) {
    return kind !== "twin";
}
export const twinShareCreateInputSchema = z.object({
    subject: twinShareSubjectSchema,
    audience: twinShareAudienceInputSchema,
    snapshot: twinShareSnapshotInputSchema.optional(),
    /** A first comment in the share's thread, a note to go with it; everyone in the share hears of the share itself. */
    comment: z.object({ body: z.string().trim().min(1).max(10_000) }).strict().optional(),
    /** Scoped to the organisation and the caller: a retried create returns the same share, and another's key finds nothing. */
    idempotencyKey: z.string().trim().min(1).max(200),
}).strict().refine((input) => input.snapshot !== undefined || !twinShareNeedsSnapshot(input.subject.kind), {
    message: "An email, a page or an excerpt is shared with a snapshot.",
    path: ["snapshot"],
});
export const twinShareInboxStateSchema = z.enum(["new", "done"]);
/** How the viewer reaches the share: its sharer, one of its people, a reader of its channel, or an admin's admin read. */
export const twinShareViewerRoleSchema = z.enum(["sharer", "person", "channel_reader", "admin"]);
export const twinSharePersonSchema = z.object({
    userId: id,
    addedBy: id,
    addedAt: z.number(),
}).strict();
export const twinShareAudienceSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("people"), people: z.array(twinSharePersonSchema) }).strict(),
    z.object({ kind: z.literal("channel"), channelId: id }).strict(),
]);
export const twinShareRecordLinkSchema = z.object({ channelId: id, recordId: id }).strict();
/** The share as its viewer sees it, with what they may do. */
export const twinShareSchema = z.object({
    id,
    /** The share's thread, once anyone has written in it; Desk maps a thread's change event to its share by it. */
    threadId: id.nullable(),
    subject: twinShareSubjectSchema,
    snapshot: twinShareSnapshotSchema.nullable(),
    sharedBy: id,
    audience: twinShareAudienceSchema,
    linkedRecord: twinShareRecordLinkSchema.nullable(),
    createdAt: z.number(),
    updatedAt: z.number(),
    lastActivityAt: z.number(),
    viewer: z.object({
        role: twinShareViewerRoleSchema,
        /** The viewer's own Inbox entry: a people share's people have one, a channel share's readers and admins none. */
        inboxState: twinShareInboxStateSchema.nullable(),
        unread: z.boolean(),
        can: z.object({
            comment: z.boolean(),
            addPeople: z.boolean(),
            leave: z.boolean(),
            move: z.boolean(),
            delete: z.boolean(),
            replaceSnapshot: z.boolean(),
            removeSnapshot: z.boolean(),
            linkRecord: z.boolean(),
        }).strict(),
    }).strict(),
}).strict();
export const twinShareResponseSchema = z.object({ share: twinShareSchema }).strict();
export const twinShareCreateResponseSchema = z.object({ share: twinShareSchema, created: z.boolean() }).strict();
export const twinShareUpdateInputSchema = z.object({
    /** A new snapshot, or null to remove it; the thread stays. */
    snapshot: twinShareSnapshotInputSchema.nullable().optional(),
    linkedRecord: twinShareRecordLinkSchema.optional(),
}).strict().refine((input) => input.snapshot !== undefined || input.linkedRecord !== undefined, {
    message: "An update replaces or removes the snapshot, or links a record.",
});
export const twinShareMoveInputSchema = z.object({ channelId: id }).strict();
/** The caller's own Inbox entry: Done or Reopen, and `seen` when they open the share, which clears its unread mark. */
export const twinShareInboxStateInputSchema = z.object({
    state: twinShareInboxStateSchema.optional(),
    seen: z.literal(true).optional(),
}).strict().refine((input) => input.state !== undefined || input.seen !== undefined, {
    message: "Set the state, mark the share seen, or both.",
});
export const twinShareAddPeopleInputSchema = z.object({
    userIds: z.array(id).min(1).max(TWIN_SHARE_MAX_PEOPLE - 1),
}).strict();
/** What happened to a share, shown in its thread by time; an admin's read is recorded but never shown. */
export const twinShareActivityKindSchema = z.enum([
    "created",
    "shared_again",
    "people_added",
    "person_removed",
    "left",
    "moved",
    "snapshot_replaced",
    "snapshot_removed",
    "record_linked",
    "admin_read",
]);
/** One line of a share's activity, which its thread shows by time beside the comments. */
export const twinShareActivityEntrySchema = z.object({
    kind: twinShareActivityKindSchema.exclude(["admin_read"]),
    actorId: id,
    /** The people added or removed, or those who were in a people share moved to a channel. */
    people: z.array(id),
    channelId: id.optional(),
    record: twinShareRecordLinkSchema.optional(),
    at: z.number(),
}).strict();
/** A share's thread, read through the share's check, and its activity lines. */
export const twinShareThreadResponseSchema = z.object({
    thread: channelFeedThreadSchema,
    activity: z.array(twinShareActivityEntrySchema),
}).strict();
/**
 * A subject as `twin.shares.list` takes it (`?subject=`): `email:<message-id>`, `mailbox:<connection id>:<message-id>`,
 * `web:<address>` or `twin:<type>:<id>`, normalised as a share's subject is, so its key finds the same shares.
 */
export function twinShareSubjectKeyFromFilter(value) {
    const separator = value.indexOf(":");
    const prefix = separator > 0 ? value.slice(0, separator) : "";
    const rest = value.slice(separator + 1);
    switch (prefix) {
        case "email": {
            const messageId = normaliseTwinShareMessageId(rest);
            return messageId ? `email:${messageId}` : null;
        }
        case "mailbox": {
            const split = rest.indexOf(":");
            const connectionId = split > 0 ? rest.slice(0, split) : "";
            const messageId = normaliseTwinShareMessageId(rest.slice(split + 1));
            return connectionId && connectionId.length <= 200 && messageId ? `mailbox:${connectionId}:${messageId}` : null;
        }
        case "web": {
            const normalised = normaliseTwinShareWebAddress(rest);
            return normalised.ok ? `web:${normalised.address}` : null;
        }
        case "twin": {
            const split = rest.indexOf(":");
            const type = twinShareTwinTypeSchema.safeParse(split > 0 ? rest.slice(0, split) : "");
            const subjectId = rest.slice(split + 1).trim();
            return type.success && subjectId && subjectId.length <= 200 ? `twin:${type.data}:${subjectId}` : null;
        }
        default:
            return null;
    }
}
export const twinSharesListQuerySchema = z.object({
    /** Shares about this subject that the caller takes part in, people and channel shares alike. */
    subject: z.string().trim().min(1).max(4_200).optional(),
    /** A channel's shares, which are listed only on its page. */
    channelId: id.optional(),
    inboxState: twinShareInboxStateSchema.optional(),
    /** `organisation`: every share in the organisation, for its admins, with no content. */
    scope: z.enum(["mine", "organisation"]).default("mine"),
    /** With `scope=organisation`: the shares whose snapshot's header lines name this address (an erasure request). */
    address: z.string().trim().min(3).max(320).optional(),
    cursor: z.string().trim().min(1).max(500).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).strict().refine((query) => query.scope === "organisation"
    ? query.subject === undefined && query.channelId === undefined && query.inboxState === undefined
    : query.address === undefined, { message: "An organisation list takes only an address; yours takes no address." });
export const twinShareListItemSchema = z.object({
    id,
    threadId: id.nullable(),
    subject: twinShareSubjectSchema,
    title: z.string().nullable(),
    sharedBy: id,
    audience: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("people"), peopleCount: z.number().int().positive() }).strict(),
        z.object({ kind: z.literal("channel"), channelId: id }).strict(),
    ]),
    commentCount: z.number().int().nonnegative(),
    latestComment: z.object({ authorId: id, createdAt: z.number(), firstLine: z.string() }).strict().nullable(),
    /** Comments in the thread the caller hasn't read. */
    unreadCount: z.number().int().nonnegative(),
    /** The caller's Inbox entry, for a people share. */
    inboxState: twinShareInboxStateSchema.nullable(),
    unread: z.boolean(),
    createdAt: z.number(),
    lastActivityAt: z.number(),
}).strict();
export const twinSharesListResponseSchema = z.object({
    shares: z.array(twinShareListItemSchema),
    nextCursor: z.string().nullable(),
}).strict();
/** An admin's organisation list: ids, the sharer, the number of people and the dates, never a title or comment. */
export const twinShareOrganisationListResponseSchema = z.object({
    shares: z.array(z.object({
        id,
        sharedBy: id,
        audienceKind: z.enum(["people", "channel"]),
        peopleCount: z.number().int().nonnegative(),
        createdAt: z.number(),
        lastActivityAt: z.number(),
        matchedAddress: z.string().optional(),
    }).strict()),
    nextCursor: z.string().nullable(),
}).strict();
